// pages/SharedQuestionsPage.js
// ─────────────────────────────────────────────────────────────
// Receives a WhatsApp-shared link with encoded question IDs.
//
// URL format:
//   /shared-questions?q=<base64url-encoded-ids>
//
// Example:
//   Teacher selects Q IDs: [101, 205, 317]
//   Encoded: btoa("101,205,317") → URL-safe base64
//   Link:    https://virtualkalvi.com/shared-questions?q=MTAxLDIwNSwzMTc
//
// This page:
//   1. Reads `q` param from URL
//   2. Decodes to get question IDs (IDs are never shown in plain text to user)
//   3. Calls GET /dynamictwomarkbyids?ids=101,205,317  ← NEW backend API
//   4. Shows questions interactively (student can answer, see explanation)
//
// Backend API required:
//   GET /dynamictwomarkbyids?ids=<comma-separated-ids>
//   Response: array of question objects (same shape as /dynamictwomark)
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BASE_API_URL, API_HEADERS } from "./config";

// ── Theme (same dark theme as VKTwoMarkListPage) ─────────────
var T = {
  bg:        "#0d1117",
  surface:   "#161b22",
  border:    "#30363d",
  accent:    "#58a6ff",
  accentDk:  "#1f6feb",
  text:      "#e6edf3",
  muted:     "#8b949e",
  correct:   "#238636",
  correctBg: "#0d2818",
  wrong:     "#da3633",
  wrongBg:   "#2d1216",
  autoCorr:  "#9e6a03",
  autoCBg:   "#2d2000",
  card:      "#1c2128",
  error:     "#f85149",
  wa:        "#25D366",
};

// ── Decode the encoded ID string from URL ────────────────────
function decodeIds(encoded) {
  try {
    // Restore base64 padding and standard characters
    var b64     = encoded.replace(/-/g, "+").replace(/_/g, "/");
    var padded  = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    var decoded = atob(padded);
    return decoded.split(",").map(function(s){ return s.trim(); }).filter(Boolean);
  } catch(e) {
    return [];
  }
}

// ── TTS ───────────────────────────────────────────────────────
function speakText(text, medium) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  var clean  = text.replace(/^(Eng|Tam)-/i, "").trim();
  var utter  = new SpeechSynthesisUtterance(clean);
  var tamil  = medium && medium.toLowerCase() === "tamil";
  utter.lang = tamil ? "ta-IN" : "en-US";
  utter.rate = 0.9;
  var voices = window.speechSynthesis.getVoices();
  var match  = voices.find(function(v){ return tamil ? v.lang.startsWith("ta") : v.lang.startsWith("en-US"); });
  if (match) utter.voice = match;
  window.speechSynthesis.speak(utter);
}

// ── Single Interactive Question Card ────────────────────────
function SharedCard(props) {
  var q   = props.q;
  var num = props.num;

  var _ans   = useState(null);  var ans    = _ans[0];  var setAns    = _ans[1];
  var _status= useState(null);  var status = _status[0]; var setStatus = _status[1];
  var _locked= useState(false); var locked = _locked[0]; var setLocked = _locked[1];
  var _hint  = useState(false); var hint   = _hint[0];  var setHint   = _hint[1];
  var _spkg  = useState(false); var spkg   = _spkg[0];  var setSpkg   = _spkg[1];

  var _opts = useState(function(){
    var raw = [q.option_a, q.option_b, q.option_c, q.option_d].filter(Boolean);
    for (var i = raw.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = raw[i]; raw[i] = raw[j]; raw[j] = tmp;
    }
    return raw;
  });
  var opts = _opts[0];

  var qText  = (q.question_text || "").replace(/^(Eng|Tam)-/i, "").trim();
  var labels = ["A","B","C","D"];
  var tamil  = q.medium && q.medium.toLowerCase() === "tamil";

  function handleSelect(optText) {
    if (locked) return;
    var correct = optText === q.correct_option;
    setAns(optText);
    setStatus(correct ? "correct" : "wrong");
    setLocked(true);
  }

  function handleSpeak() {
    if (spkg) { window.speechSynthesis.cancel(); setSpkg(false); return; }
    var txt = (q.question_text_read || qText) + ". " + (q.question_explain || "");
    speakText(txt, q.medium);
    setSpkg(true);
    setTimeout(function(){ setSpkg(false); }, (txt.length * 70) + 2000);
  }

  function optClass(optText) {
    if (!locked) return "sc-opt";
    if (ans === optText && status === "correct") return "sc-opt sc-correct";
    if (ans === optText && status === "wrong")   return "sc-opt sc-wrong";
    if (status === "wrong" && optText === q.correct_option) return "sc-opt sc-show-correct";
    return "sc-opt sc-locked";
  }

  return React.createElement("div", { className:"sc-card" },

    // Top accent bar
    React.createElement("div", {
      style:{ height:3, background:"linear-gradient(90deg,"+T.accent+","+T.accentDk+")" }
    }),

    React.createElement("div", { style:{ padding:"18px 20px" } },

      // Header row: number + chapter tags + action buttons
      React.createElement("div", {
        style:{ display:"flex", alignItems:"center", justifyContent:"space-between",
                marginBottom:12, flexWrap:"wrap", gap:8 }
      },

        // Tags
        React.createElement("div", { style:{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" } },
          React.createElement("span", {
            style:{ fontSize:11,fontWeight:700,color:T.accent,
                    background:"rgba(88,166,255,0.1)",border:"1px solid rgba(88,166,255,0.2)",
                    padding:"3px 10px",borderRadius:999 }
          }, "Q " + num),
          q.chapter ? React.createElement("span", {
            style:{ fontSize:11,fontWeight:600,color:T.muted,
                    background:"rgba(139,148,158,0.08)",border:"1px solid "+T.border,
                    padding:"3px 9px",borderRadius:999 }
          }, q.chapter) : null,
          q.unit ? React.createElement("span", {
            style:{ fontSize:11,fontWeight:600,color:T.muted,
                    background:"rgba(139,148,158,0.08)",border:"1px solid "+T.border,
                    padding:"3px 9px",borderRadius:999 }
          }, q.unit) : null,
          React.createElement("span", {
            style:{
              fontSize:11,fontWeight:700,
              color: tamil ? "#d29922" : "#58a6ff",
              background: tamil ? "rgba(210,153,34,0.1)" : "rgba(88,166,255,0.08)",
              border:"1px solid "+(tamil?"rgba(210,153,34,0.3)":"rgba(88,166,255,0.2)"),
              padding:"3px 9px",borderRadius:999,
            }
          }, tamil ? "🇮🇳 Tamil" : "🇬🇧 English")
        ),

        // Action buttons: Speak + Hint
        React.createElement("div", { style:{ display:"flex", gap:7 } },
          React.createElement("button", {
            className:"sc-icon-btn"+(spkg?" sc-spk-on":""),
            onClick: handleSpeak,
            title: tamil ? "தமிழில் படிக்கவும்" : "Read aloud",
          }, "🔊"),
          React.createElement("button", {
            className:"sc-icon-btn"+(hint?" sc-hint-on":""),
            onClick: function(){ setHint(function(h){ return !h; }); },
            title:"Explanation",
          }, "💡")
        )
      ),

      // Hint / Explanation (above question)
      hint && q.question_explain ? React.createElement("div", {
        style:{
          background:"rgba(210,153,34,0.07)", border:"1px solid rgba(210,153,34,0.22)",
          borderRadius:10, padding:"10px 14px", marginBottom:14,
          fontSize:13, color:"#d29922",
          fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif", lineHeight:1.7,
        }
      }, "💡 " + q.question_explain) : null,

      // Question text
      React.createElement("p", {
        style:{
          fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif",
          fontSize:16, fontWeight:600, lineHeight:1.75,
          color:T.text, marginBottom:16,
        }
      }, qText),

      // Options
      opts.map(function(optText, i) {
        return React.createElement("div", {
          key: i,
          className: optClass(optText),
          onClick: function(){ handleSelect(optText); },
        },
          React.createElement("span", {
            style:{
              display:"inline-flex", alignItems:"center", justifyContent:"center",
              width:24, height:24, borderRadius:6,
              background:"rgba(88,166,255,0.1)", color:T.accent,
              fontSize:12, fontWeight:700, flexShrink:0,
            }
          }, labels[i]),
          React.createElement("span", {
            style:{ flex:1, fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif" }
          }, optText),
          ans===optText && status==="correct"
            ? React.createElement("span", { style:{ color:"#3fb950",fontWeight:700,fontSize:17 } }, "✓")
            : null,
          ans===optText && status==="wrong"
            ? React.createElement("span", { style:{ color:"#f85149",fontWeight:700,fontSize:17 } }, "✗")
            : null,
          status==="wrong" && optText===q.correct_option
            ? React.createElement("span", { style:{ color:"#d29922",fontWeight:700,fontSize:14 } }, "✓")
            : null
        );
      }),

      // Result banner
      locked ? React.createElement("div", {
        style:{
          marginTop:12, padding:"9px 14px", borderRadius:10,
          fontSize:13, fontWeight:700, textAlign:"center",
          background: status==="correct" ? T.correctBg : T.wrongBg,
          color:      status==="correct" ? "#3fb950"   : "#f85149",
          border:"1px solid "+(status==="correct" ? T.correct : T.wrong),
        }
      },
        status==="correct"
          ? (tamil ? "✓ சரியான விடை!" : "✓ Correct!")
          : (tamil
              ? "✗ தவறான விடை — விடை: " + q.correct_option
              : "✗ Wrong — Answer: " + q.correct_option)
      ) : null
    )
  );
}

// ── Score bar ────────────────────────────────────────────────
function ScoreSummary(props) {
  var correct = props.correct;
  var wrong   = props.wrong;
  var att     = props.att;
  var total   = props.total;
  var pct     = att === 0 ? 0 : Math.round((correct / att) * 100);
  return React.createElement("div", {
    style:{
      background:"linear-gradient(135deg,"+T.surface+","+T.card+")",
      border:"1px solid "+T.border, borderRadius:16,
      padding:"14px 20px", marginBottom:18,
      display:"flex", alignItems:"center", gap:18, flexWrap:"wrap",
    }
  },
    React.createElement("div", {
      style:{
        fontSize:36, fontWeight:800, color:T.accent,
        minWidth:60, textAlign:"center",
      }
    }, pct + "%"),
    React.createElement("div", { style:{ display:"flex", gap:20, flexWrap:"wrap" } },
      [
        { label:"Correct",   val:correct,      color:"#3fb950"   },
        { label:"Wrong",     val:wrong,         color:"#f85149"   },
        { label:"Attempted", val:att,           color:T.muted     },
        { label:"Total",     val:total,         color:T.accent    },
      ].map(function(s, i) {
        return React.createElement("div", {
          key:i,
          style:{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }
        },
          React.createElement("span", {
            style:{ fontSize:22, fontWeight:800, color:s.color, lineHeight:1 }
          }, s.val),
          React.createElement("span", {
            style:{ fontSize:11, color:T.muted, fontWeight:600,
                    textTransform:"uppercase", letterSpacing:"0.06em" }
          }, s.label)
        );
      })
    )
  );
}

// ── Main Page ────────────────────────────────────────────────
export default function SharedQuestionsPage() {
  var navigate      = useNavigate();
  var searchParams  = useSearchParams()[0];
  var encoded       = searchParams.get("q") || "";

  var [questions, setQuestions] = useState([]);
  var [loading,   setLoading]   = useState(true);
  var [error,     setError]     = useState("");
  var [att,       setAtt]       = useState(0);
  var [correct,   setCorrect]   = useState(0);

  // Decode IDs from URL param (never show raw IDs to user)
  var ids = decodeIds(encoded);

  useEffect(function() {
    if (!encoded || ids.length === 0) {
      setError("Invalid or missing share link. Please ask the sender for a new link.");
      setLoading(false);
      return;
    }

    // ── Call the new API endpoint with decoded IDs ──────────────
    // Backend: GET /dynamictwomarkbyids?ids=101,205,317
    var url = BASE_API_URL + "/dynamictwomarkbyids?ids=" + ids.join(",");

    fetch(url, { headers: API_HEADERS })
      .then(function(r) {
        if (!r.ok) throw new Error("Server error: HTTP " + r.status);
        return r.json();
      })
      .then(function(data) {
        var rows = Array.isArray(data) ? data : (data.data || []);
        if (rows.length === 0) throw new Error("No questions found for this link.");
        setQuestions(rows);
        setLoading(false);
      })
      .catch(function(e) {
        setError(e.message);
        setLoading(false);
      });
  }, [encoded]); // eslint-disable-line

  function handleAnswer(isCorrect) {
    setAtt(function(a){ return a + 1; });
    if (isCorrect) setCorrect(function(c){ return c + 1; });
  }

  var FONTS = `
    @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;600;700;800&family=Noto+Serif+Tamil:wght@400;600;700&display=swap');
    *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
    body { background:${T.bg}; color:${T.text}; font-family:'Baloo 2',sans-serif; }
    @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
    @keyframes spin   { to{transform:rotate(360deg)} }

    .sc-card {
      background:${T.card}; border:1.5px solid ${T.border};
      border-radius:18px; margin-bottom:16px; overflow:hidden;
      animation:fadeUp 0.35s cubic-bezier(.22,1,.36,1) both;
    }

    .sc-opt {
      display:flex; align-items:center; gap:10px;
      padding:11px 14px; border-radius:10px;
      border:1.5px solid ${T.border}; background:${T.bg};
      cursor:pointer; margin-bottom:8px;
      font-family:'Noto Serif Tamil','Baloo 2',sans-serif;
      font-size:14px; color:${T.text};
      transition:border-color 0.15s, background 0.15s, transform 0.12s;
      user-select:none;
    }
    .sc-opt:hover:not(.sc-locked):not(.sc-correct):not(.sc-wrong):not(.sc-show-correct) {
      border-color:${T.accent}; background:#111d2e; transform:translateX(3px);
    }
    .sc-opt.sc-locked        { cursor:default; }
    .sc-opt.sc-correct       { border-color:${T.correct}; background:${T.correctBg}; color:#3fb950; }
    .sc-opt.sc-wrong         { border-color:${T.wrong};   background:${T.wrongBg};   color:#f85149; }
    .sc-opt.sc-show-correct  { border-color:${T.autoCorr}; background:${T.autoCBg}; color:#d29922; }

    .sc-icon-btn {
      display:flex; align-items:center; justify-content:center;
      width:34px; height:34px; border-radius:9px;
      border:1.5px solid ${T.border}; background:${T.surface};
      cursor:pointer; font-size:15px; color:${T.muted};
      transition:border-color 0.15s, color 0.15s;
    }
    .sc-icon-btn:hover { border-color:${T.accent}; color:${T.accent}; }
    .sc-spk-on  { border-color:${T.accent}; background:rgba(88,166,255,0.1); color:${T.accent}; }
    .sc-hint-on { border-color:#d29922; background:rgba(210,153,34,0.08); color:#d29922; }
  `;

  // ── Loading ────────────────────────────────────────────────
  if (loading) return React.createElement("div", {
    style:{ minHeight:"100vh", background:T.bg,
            display:"flex", flexDirection:"column",
            alignItems:"center", justifyContent:"center", gap:16 }
  },
    React.createElement("style", null, FONTS),
    React.createElement("div", {
      style:{ width:44, height:44, borderRadius:"50%",
              border:"3px solid "+T.border, borderTop:"3px solid "+T.accent,
              animation:"spin 0.8s linear infinite" }
    }),
    React.createElement("p", {
      style:{ color:T.muted, fontSize:14, fontWeight:600 }
    }, "Loading shared questions…")
  );

  // ── Error ──────────────────────────────────────────────────
  if (error || ids.length === 0) return React.createElement("div", {
    style:{
      minHeight:"100vh", background:T.bg,
      display:"flex", alignItems:"center", justifyContent:"center",
      flexDirection:"column", gap:12, padding:32, textAlign:"center",
    }
  },
    React.createElement("style", null, FONTS),
    React.createElement("div", { style:{ fontSize:40, marginBottom:4 } }, "⚠️"),
    React.createElement("div", {
      style:{ color:T.error, fontWeight:700, fontSize:15 }
    }, "Link Error"),
    React.createElement("div", {
      style:{ color:T.muted, fontSize:13, maxWidth:380, lineHeight:1.6 }
    }, error || "This link appears to be invalid or expired. Ask the sender to share a fresh link."),
    React.createElement("button", {
      style:{
        marginTop:16, padding:"10px 24px", borderRadius:24, border:"none",
        background:T.accent, color:"#fff", fontWeight:700,
        fontSize:14, cursor:"pointer",
      },
      onClick:function(){ navigate(-1); }
    }, "← Go Back")
  );

  // ── Main render ────────────────────────────────────────────
  var subject  = questions[0] ? questions[0].subject    : "Questions";
  var grade    = questions[0] ? questions[0].grade_level : "";
  var chapter  = questions[0] ? questions[0].chapter     : "";

  return React.createElement("div", {
    style:{ minHeight:"100vh", background:T.bg, paddingBottom:40 }
  },
    React.createElement("style", null, FONTS),

    // ── Sticky header ─────────────────────────────────────────
    React.createElement("div", {
      style:{
        background:"rgba(13,17,23,0.96)", borderBottom:"1px solid "+T.border,
        padding:"0 20px", height:58, display:"flex",
        alignItems:"center", justifyContent:"space-between",
        position:"sticky", top:0, zIndex:50, backdropFilter:"blur(12px)",
      }
    },
      React.createElement("div", { style:{ display:"flex", alignItems:"center", gap:10 } },
        React.createElement("button", {
          style:{ background:"none", border:"none", color:T.muted,
                  fontSize:20, cursor:"pointer", lineHeight:1 },
          onClick:function(){ navigate(-1); }
        }, "←"),
        React.createElement("div", null,
          React.createElement("div", {
            style:{ fontWeight:800, fontSize:15, color:T.text }
          }, subject + (grade ? " · Grade " + grade : "")),
          React.createElement("div", {
            style:{ fontSize:11, color:T.muted }
          }, chapter || "Shared Questions")
        )
      ),
      React.createElement("div", {
        style:{
          fontSize:12, fontWeight:700,
          background:"rgba(88,166,255,0.12)", border:"1px solid rgba(88,166,255,0.25)",
          color:T.accent, padding:"3px 12px", borderRadius:999,
        }
      }, questions.length + " Questions")
    ),

    // ── Shared-by banner ──────────────────────────────────────
    React.createElement("div", {
      style:{
        background:"linear-gradient(135deg,rgba(37,211,102,0.12),rgba(18,140,126,0.08))",
        border:"none", borderBottom:"1.5px solid rgba(37,211,102,0.2)",
        padding:"10px 20px", textAlign:"center",
      }
    },
      React.createElement("span", {
        style:{ fontSize:13, fontWeight:700, color:"#25D366", display:"flex",
                alignItems:"center", justifyContent:"center", gap:6 }
      },
        "📲",
        React.createElement("span", null,
          questions.length + " important questions shared via VirtualKalvi"
        )
      )
    ),

    // ── Content ───────────────────────────────────────────────
    React.createElement("div", {
      style:{ maxWidth:700, margin:"0 auto", padding:"20px 16px" }
    },

      // Score summary (live as student answers)
      att > 0 ? React.createElement(ScoreSummary, {
        correct: correct,
        wrong:   att - correct,
        att:     att,
        total:   questions.length,
      }) : null,

      // Info chip (before answering)
      att === 0 ? React.createElement("div", {
        style:{
          background:"rgba(88,166,255,0.07)", border:"1px solid rgba(88,166,255,0.2)",
          borderRadius:12, padding:"10px 16px", marginBottom:16,
          fontSize:13, color:T.muted, textAlign:"center", lineHeight:1.6,
        }
      },
        "📚 Tap an option to answer each question. 💡 Tap the hint button for explanations."
      ) : null,

      // Question cards
      questions.map(function(q, idx) {
        return React.createElement(SharedCard, {
          key:    q.id || idx,
          q:      q,
          num:    idx + 1,
          onAnswer: handleAnswer,
        });
      })
    )
  );
}
